import React, { useState, useEffect, useContext, useRef } from "react";
import { useAppAuth } from "../MockAuthProvider";
import { ScenarioContext } from "../context/ScenarioContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { TooltipProvider } from "../components/ui/tooltip";
import { usePageTracking, useAnalytics } from "../hooks/useAnalytics";
import { useDarkMode } from "../hooks/useDarkMode";
import { useCoachAI } from "../hooks/useCoachAI";
import { Info, ArrowRight } from "lucide-react";

// Components
import Header from "../components/Header";
import ChatInterface from "../components/ChatInterface";
import ScenarioSelector from "../components/ScenarioSelector";
import FaqPanel from "../components/FaqPanel";

// Data
import { SCENARIOS, SCENARIO_DESCRIPTIONS } from "../data/scenarios";
import { MOODS, USER_MOOD_TOOLTIPS, MOOD_DESCRIPTIONS } from "../data/moods";
import { DIFFICULTY_LEVELS, DEFAULT_DIFFICULTY } from "../data/difficulty";


function ScenarioPractice() {
  usePageTracking("ScenarioPractice");
  const auth = useAppAuth();
  const { messages, setMessages } = useContext(ScenarioContext);
  const { darkMode, setDarkMode } = useDarkMode();
  const { loading, streaming, loadingMessage, sendMessage, getFeedback } = useCoachAI();
  const { 
    trackScenarioStarted, 
    trackScenarioSelection, 
    trackMoodSelection,
    trackFeedbackRequested 
  } = useAnalytics();
  
  // State
  const [selectedScenario, setSelectedScenario] = useState(Object.keys(SCENARIOS)[0]);
  const [selectedMood, setSelectedMood] = useState(Object.keys(MOODS)[0]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(DEFAULT_DIFFICULTY);
  const [userInput, setUserInput] = useState("");
  const [hasTyped, setHasTyped] = useState(false);
  const [activeTab, setActiveTab] = useState("faq");
  const [showFAQ, setShowFAQ] = useState(false);

  // Ref to track session start time
  const sessionStartTimeRef = useRef(Date.now());


  // Track scenario start event once per mount
  useEffect(() => {
    trackScenarioStarted(selectedScenario, selectedMood, selectedDifficulty);
  }, [trackScenarioStarted, selectedScenario, selectedMood, selectedDifficulty]);

  // Track scenario selection changes
  const handleScenarioChange = (newScenario) => {
    const previousScenario = selectedScenario;
    setSelectedScenario(newScenario);
    trackScenarioSelection(newScenario, previousScenario);
  };

  // Track mood selection changes
  const handleMoodChange = (newMood) => {
    const previousMood = selectedMood;
    setSelectedMood(newMood);
    trackMoodSelection(newMood, previousMood);
  };

  // Handle sending a message with scenario context
  const handleSend = () => {
    const scenarioDetails = SCENARIOS[selectedScenario];
    const moodInstruction = MOODS[selectedMood];
    const difficultyInstruction = DIFFICULTY_LEVELS[selectedDifficulty].prompt;
    const promptContext = `Scenario Context: ${scenarioDetails}\nConversation Tone: ${moodInstruction}\n${difficultyInstruction}`;
    
    sendMessage({
      messages,
      setMessages,
      userInput,
      setUserInput,
      promptContext,
      agentType: "roleplay",
    });
  };

  // Handle ending the conversation and getting feedback
  const handleEndConversation = () => {
    getFeedback({
      messages,
      setMessages,
      selectedScenario,
      selectedMood,
    });
  };


  return (
    <TooltipProvider>
      <div className="flex flex-col h-screen w-full text-gray-900 dark:text-gray-100 overflow-hidden relative">
        {/* Background gradient */}
        <div className="background-gradient"></div>
        {/* Fixed Header */}
        <div className="sticky top-0 z-20 bg-transparent shadow-sm">
          <Header darkMode={darkMode} setDarkMode={setDarkMode} currentPage="scenario" />
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col min-h-full pb-[120px] md:pb-[120px]">
            <div className="px-2 md:px-4 lg:px-6 2xl:px-8 pb-0">
              <div className="w-full max-w-[1200px] mx-auto">
                {/* Page Title - Prominent instruction */}
                <div className="bg-blue-50/80 dark:bg-blue-900/20 p-2 3xl:p-4 rounded-lg mb-3 3xl:mb-6 mt-2 3xl:mt-6">
                  <p className="text-sm md:text-base 3xl:text-base font-medium text-gray-900 dark:text-white text-center">
                    CoachAI will play the role of your associate. Practice your coaching conversation by selecting a scenario below.
                  </p>
                </div>

                {/* Scenario Selector - Compact */}
                <ScenarioSelector
                  selectedScenario={selectedScenario}
                  setSelectedScenario={handleScenarioChange}
                  selectedMood={selectedMood}
                  setSelectedMood={handleMoodChange}
                  selectedDifficulty={selectedDifficulty}
                  setSelectedDifficulty={setSelectedDifficulty}
                  scenarios={SCENARIOS}
                  moods={MOODS}
                  difficulties={DIFFICULTY_LEVELS}
                  tooltips={USER_MOOD_TOOLTIPS}
                  scenarioDescriptions={SCENARIO_DESCRIPTIONS}
                  moodDescriptions={MOOD_DESCRIPTIONS}
                />

                {/* Chat Interface - Compact */}
                <div className="mt-3 2xl:mt-6 mb-2 2xl:mb-6">
                  <ChatInterface
                    messages={messages}
                    loading={loading}
                    streaming={streaming}
                    loadingMessage={loadingMessage}
                    userInput={userInput}
                    setUserInput={setUserInput}
                    handleSend={handleSend}
                    hasTyped={hasTyped}
                    setHasTyped={setHasTyped}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom with Input - Compact */}
        <div className="fixed bottom-4 md:bottom-8 left-0 right-3 z-10 px-2 md:px-4 lg:px-6 2xl:px-8 pointer-events-none">
          <div className="w-full max-w-[1200px] mx-auto">
            <div className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm rounded-lg border border-orange-200/50 dark:border-blue-700/30 p-2 mb-2 pointer-events-auto">
              <div className="flex gap-2">
                <Input
                  value={userInput}
                  onChange={(e) => {
                    if (!hasTyped && e.target.value.trim().length > 0) {
                      setHasTyped(true);
                    }
                    setUserInput(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={!hasTyped ? "Start typing here..." : ""}
                  className={`flex-1 h-8 md:h-10 px-2 md:px-3 text-xs md:text-sm border rounded-lg ${hasTyped ? 'bg-white/70 dark:bg-gray-900/70' : 'bg-transparent'} text-gray-800 dark:text-gray-200`}
                />
                <Button
                  onClick={handleSend}
                  className="bg-white/70 hover:bg-white/80 dark:bg-gray-900/70 h-8 md:h-10 w-8 md:w-10 rounded-lg"
                >
                  <ArrowRight className="h-3 md:h-4 w-3 md:w-4" />
                </Button>
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 pointer-events-auto">
              <div className="flex items-center gap-1 md:gap-2">
                <button
                  className="md:hidden rounded-full bg-gray-200 dark:bg-gray-800 p-1 w-7 h-7 flex items-center justify-center"
                  onClick={() => setShowFAQ(!showFAQ)}
                >
                  <Info className="h-4 w-4" />
                </button>
              </div>
              <Button
                onClick={handleEndConversation}
                className="bg-gray-800 hover:bg-orange-500 dark:bg-white h-7 md:h-9 px-2 md:px-4 text-xs md:text-sm font-medium rounded-lg"
                style={{ color: darkMode ? '#000000' : '#ffffff' }}
              >
                End & Get Feedback
              </Button>
            </div>
          </div>
        </div>
        
        {/* Footer with Feedback and Info buttons - hidden on mobile to avoid overlap with input */}
        <div className="hidden md:flex w-full justify-between items-center px-6 py-4 bg-transparent">
          {/* Left: Feedback Button */}
          <a
            href="https://amazoninteu.qualtrics.com/jfe/form/SV_51MBlyNYtlDixH8"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-900 dark:hover:text-gray-300 hover:underline transition-colors text-sm font-medium"
            style={{
              color: darkMode ? "#ffffff" : "#000000"
            }}
          >
            Give Feedback
          </a>

          {/* Center: Disclaimer */}
          <div className="text-black-500 dark:text-white-400 text-sm text-center">
            <div className="mb-1">Optimized for Customer Service. Open to All.</div>
            <div>CoachAI can make mistakes. Always check important info.</div>
          </div>

          {/* Right: Info Button */}
          <button
            className="rounded-full p-2 w-12 h-12 flex items-center justify-center transition-shadow shadow-[0_0_10px_rgba(0,0,0,0.15)] hover:shadow-[0_0_14px_rgba(0,0,0,0.25)] dark:shadow-[0_0_10px_rgba(255,255,255,0.15)] dark:hover:shadow-[0_0_14px_rgba(255,255,255,0.25)]"
            onClick={() => setShowFAQ(!showFAQ)}
          >
            <Info className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          </button>
        </div>

        {/* FAQ Panel */}
        <FaqPanel
          showFAQ={showFAQ}
          setShowFAQ={setShowFAQ}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>
    </TooltipProvider>
  );
}

export default ScenarioPractice;
